/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box } from '@mui/material';
import type { FC } from 'react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTafTranslation } from '../../utils/i18n';
import {
  BaseTextField,
  TEXTFIELD_MIN_WIDTH,
  TEXTFIELD_WIDTH_PERCENTAGE,
} from '../TafForm/TafFormRow/TafFormTextField';

export interface TafRemarkFieldProps {
  disabled?: boolean;
  isEditor?: boolean;
}

const TafRemarkField: FC<TafRemarkFieldProps> = ({ disabled, isEditor }) => {
  const { t } = useTafTranslation();
  const { control, setValue, trigger } = useFormContext();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginTop: '8px',
        marginRight: 'max(28px, 3.5%)',
        paddingLeft: { xs: '2px', lg: '10px' },
        paddingRight: { xs: '2px', lg: '10px' },
      }}
    >
      <Controller
        control={control}
        name="remark"
        render={({ field }): React.ReactElement => {
          return (
            <BaseTextField
              disabled={disabled}
              inputRef={field.ref}
              label={t('remark')}
              name="remark"
              onBlur={() => {
                void trigger();
                field.onBlur();
              }}
              onChange={(event) => {
                field.onChange(event.currentTarget.value.toUpperCase());
              }}
              onKeyDown={(event) => {
                if (
                  event.key.toUpperCase() === 'N' &&
                  !field.value &&
                  !event.ctrlKey &&
                  !event.altKey &&
                  !event.metaKey
                ) {
                  event.preventDefault();
                  setValue('remark', 'NO NAF DUE TO LACK OF DATA');
                }
              }}
              size="small"
              slotProps={{
                htmlInput: {
                  'aria-label': 'remark',
                },
              }}
              sx={{
                // the size of a taf textfield
                '--taf-textfield-size': `max(${TEXTFIELD_WIDTH_PERCENTAGE}%, ${TEXTFIELD_MIN_WIDTH})`,

                // the width of the row minus the drag thingie
                '--total-row-size': 'calc(100% - 62px)',

                // calculates the size of the taf remark field so that it nicely aligns with the taf textfield rows
                // even when these rows are wrapped.
                width: isEditor
                  ? `round(down, var(--total-row-size), var(--taf-textfield-size))`
                  : '100%',

                marginLeft: 'auto',
              }}
              textAlign="left"
              value={field.value || ''}
            />
          );
        }}
      />
    </Box>
  );
};

export default TafRemarkField;
